import { getToken } from '@/api/http-service/PrepareAPIService'
import { getWorkerAPI } from '@/worker/client'
import {
  CellsResponseData,
  CommitCurrentChangesParams,
  GetAllRecordsParams,
  GetCellValuesParams,
  GetCountsParams,
  GetCountsResponse,
  GetRecordsParams,
  RecordOperations,
  RowChangeDetail,
  RowPatchDetail,
} from './interface'

import {
  formatTurntableDiffRowsFromConcise,
  formatTurntableRowsFromConcise,
} from './data-utils'

export const bufferMemcsvDriver: RecordOperations = {
  fetchRecords: async (params: GetRecordsParams, columnConfig: any[]) => {
    try {
      const workerAPI = getWorkerAPI()

      // Get the current auth token from Better Auth
      const token = getToken()
      if (!token) {
        throw new Error(
          'Authentication token not available. Please ensure you are authenticated.'
        )
      }
      const { setExtraKeys, ...reducedParams } = params
      let keysToAdd: string[] = []

      // Call getRecords directly with the sheetId, params, and token
      // This avoids trying to serialize/return the Database object
      const results = await workerAPI.getRecords(
        params.sheetId,
        { ...reducedParams },
        token
      )

      const { transformedResult, allKeys, result } = results
      if (
        params.transformationRules &&
        (params.transformationRules.some((rule) => rule.type === 'function') ||
          params.transformationRules.some(
            (rule) => rule.type === 'apply-changes'
          ))
      ) {
        // Filter keys not already in column config
        keysToAdd = allKeys.filter(
          (k) =>
            k !== 'id' &&
            k !== '_source' &&
            !columnConfig.find((c) => c.value === k)
        )
        // Only call setExtraKeys if we actually have new keys to add
        // This prevents unnecessary state updates
        if (keysToAdd.length > 0 && setExtraKeys) {
          // Create a stable reference by creating a new array
          setExtraKeys([...keysToAdd])
        }
      }

      // Use a consistent way to generate column config to prevent unnecessary re-renders
      const usedColumnConfig =
        keysToAdd.length > 0
          ? [
              ...columnConfig,
              ...keysToAdd.map((k) => ({
                value: k,
                label: k,
                type: 'text',
              })),
            ]
          : columnConfig

      const offset = params.pageNumber * params.pageSize
      const cells =
        params.transformationRules.length > 0
          ? formatTurntableDiffRowsFromConcise(
              result,
              transformedResult,
              offset,
              usedColumnConfig
            )
          : formatTurntableRowsFromConcise(result, offset, usedColumnConfig)
      return cells
    } catch (error) {
      console.error('Error fetching records:', error)
      return []
    }
  },

  addRecords: async (
    sheetId: string,
    changes: RowChangeDetail[],
    columnConfig: any[]
  ): Promise<any> => {
    const workerAPI = getWorkerAPI()
    const token = getToken()
    if (!token) {
      throw new Error(
        'Authentication token not available. Please ensure you are authenticated.'
      )
    }

    // TODO: Need to consider this interface as it might need to pass in sheetId so we can use database upsert
    return { data: { success: true } }
  },

  updateRecords: async (
    sheetId: string,
    changes: RowChangeDetail[],
    columnConfig: any[]
  ): Promise<any> => {
    const workerAPI = getWorkerAPI()
    const token = getToken()
    if (!token) {
      throw new Error(
        'Authentication token not available. Please ensure you are authenticated.'
      )
    }
    // TODO: Need to consider this interface as it might need to pass in sheetId so we can use database upsert
    return { data: { success: true } }
  },

  deleteRecords: async (payload: {
    sheetId: string
    recordIds: string[]
  }): Promise<any> => {
    return { data: { success: true } }
  },

  downloadRecords: async (payload: {
    sheetId: string
    filter?: string
  }): Promise<any> => {
    return new Blob([''], { type: 'text/csv' })
  },

  addOrUpdateRecords: async (
    sheetId: string,
    changeEvent: { changes: RowPatchDetail[] }
  ): Promise<any> => {
    const workerAPI = getWorkerAPI()
    const token = getToken()
    if (!token) {
      throw new Error(
        'Authentication token not available. Please ensure you are authenticated.'
      )
    }
    const { changes } = changeEvent

    const result = await workerAPI.upsertRecords(sheetId, changes, token)
    return result
  },

  hydrateAllRecords: async (params: GetAllRecordsParams): Promise<any> => {
    const workerAPI = getWorkerAPI()
    const token = getToken()
    if (!token) {
      throw new Error(
        'Authentication token not available. Please ensure you are authenticated.'
      )
    }

    await workerAPI.hydrateAllRecords(params.sheetId, params.sheet, token)

    return { data: { success: true } }
  },

  commitCurrentChanges: async (
    params: CommitCurrentChangesParams
  ): Promise<any> => {
    const workerAPI = getWorkerAPI()
    const token = getToken()
    if (!token) {
      throw new Error(
        'Authentication token not available. Please ensure you are authenticated.'
      )
    }

    await workerAPI.commitCurrentChanges(params.sheetId, params, token)
    return { data: { success: true } }
  },

  fetchMappingPreview: async (payload: {
    sheetId: string
    filter?: string
  }): Promise<any> => {
    return { data: { success: true } }
  },

  getCounts: async (params: GetCountsParams): Promise<GetCountsResponse> => {
    const workerAPI = getWorkerAPI()
    const token = getToken()
    if (!token) {
      throw new Error(
        'Authentication token not available. Please ensure you are authenticated.'
      )
    }

    return workerAPI.getCounts(params.sheetId, params, token)
  },

  getCellValues: async (
    params: GetCellValuesParams
  ): Promise<CellsResponseData> => {
    const workerAPI = getWorkerAPI()
    const token = getToken()
    if (!token) {
      throw new Error(
        'Authentication token not available. Please ensure you are authenticated.'
      )
    }

    return workerAPI.getCellValues(params.sheetId, params, token)
  },

  registerRecordUpdate: async (payload: {
    sheetId: string
    recordId: string
    values: Record<string, any>
  }): Promise<any> => {
    const workerAPI = getWorkerAPI()
    const token = getToken()
    if (!token) {
      throw new Error(
        'Authentication token not available. Please ensure you are authenticated.'
      )
    }
    return workerAPI.registerRecordUpdate(
      payload.sheetId,
      payload.recordId,
      payload.values,
      token
    )
  },

  getRecordById: async (payload: {
    sheetId: string
    recordId: string
  }): Promise<any> => {
    const workerAPI = getWorkerAPI()
    const token = await getToken()
    return workerAPI.getRecordById(payload.sheetId, payload.recordId, token)
  },
}
